import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

// eslint-disable-next-line import/order
import BgTop from '../../assets/images/roadmap/roadmap-bg.png';

import {
  getShortName,
  latestBestFitRecord,
  useRoadmapAiContext,
} from '../../hooks/use-roadmap-ai.hooks';
import { AlternateCareer, CareerRoadmap } from '../../redux/slice/roadmap-ai/roadmap-ai.type';
import { AuthenticatedPage } from '../AuthenticatedPage';

// import { CareerMatching } from './ReverseCareerMatching';
import { RoadmapPage1 } from './RoadmapPage1';

interface SectorOption {
  name: string;
  shortName: string;
}

export const RoadMapDashboard = () => {
  const integrateSectors: SectorOption[] = [
    { name: 'Healthcare', shortName: 'healthcare' },
    { name: 'Retail', shortName: 'retail' },
    { name: 'Logistics', shortName: 'logistics' },
    { name: 'Manufacturing', shortName: 'manufacturing' },
    { name: 'IT/Tech-support', shortName: 'it_tech_support' },
    { name: 'Green Tech', shortName: 'green_tech' },
  ];

  const careerRoadMaps = useRoadmapAiContext();
  const BestFitRecords = latestBestFitRecord();
  const [activeButton, setActiveButton] = useState<'Emerging Pathways' | 'Top-3 Best-Fit'>(
    'Top-3 Best-Fit',
  );
  const [activeButton2, setActiveButton2] = useState<string>('healthcare');
  const [top3Sectors, setTop3Sectors] = useState<SectorOption[]>([]);

  // Compute Top-3 Best-Fit sectors
  useEffect(() => {
    if (careerRoadMaps?.career_roadmap) {
      const roadmap: CareerRoadmap = careerRoadMaps.career_roadmap;

      const sectorsWithScore = Object.entries(roadmap).map(([sector, years]) => {
        const yearValues: AlternateCareer[] = Object.values(years);
        const scores = yearValues.map((year) => Number(year.fit_score) || 0);
        const maxScore = Math.max(...scores);
        return { sector, fit_score: maxScore };
      });

      const filtered = sectorsWithScore
        .filter((s) => s.fit_score >= 70)
        .sort((a, b) => b.fit_score - a.fit_score)
        .slice(0, 3);

      const formatted = filtered.map((s) => {
        let formattedName = s.sector.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

        if (
          formattedName.toLowerCase().includes('it') ||
          formattedName.toLowerCase().includes('It')
        ) {
          formattedName = 'IT / Tech-Support';
        } else if (formattedName.toLowerCase().includes('green')) {
          formattedName = 'Green Tech';
        } else if (formattedName.toLowerCase().includes('manufacturing')) {
          formattedName = 'Manufacturing';
        } else if (formattedName.toLowerCase().includes('logistics')) {
          formattedName = 'Logistics';
        } else if (formattedName.toLowerCase().includes('retail')) {
          formattedName = 'Retail';
        } else if (formattedName.toLowerCase().includes('healthcare')) {
          formattedName = 'Healthcare';
        }

        return {
          name: formattedName,
          shortName: s.sector,
        };
      });

      setTop3Sectors(formatted);

      if (activeButton === 'Top-3 Best-Fit' && formatted.length > 0) {
        setActiveButton2(formatted[0].shortName);
      }
    }
  }, [BestFitRecords, activeButton]);

  // If Emerging Pathways record exists, set default sector
  useEffect(() => {
    if (BestFitRecords?.sector && activeButton === 'Emerging Pathways') {
      setActiveButton2(getShortName(BestFitRecords?.sector));
    }
  }, [BestFitRecords, activeButton]);

  // Determine which sectors to show in lower tabs
  const sectorsToShow = activeButton === 'Emerging Pathways' ? integrateSectors : top3Sectors;

  return (
    <motion.div
      className="relative bg-white z-0 rounded-xl py-3 px-2 items-center shadow-lg"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="relative z-10 w-full flex flex-col gap-7 items-center">
        {/* Tabs */}
        <motion.div
          className="flex flex-col justify-center gap-6 mt-5 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {/* Upper Tabs */}
          <div className="flex flex-wrap justify-center gap-3">
            {['Top-3 Best-Fit', 'Emerging Pathways'].map((tab, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveButton(tab as 'Emerging Pathways' | 'Top-3 Best-Fit')}
                className={`px-4 py-2 rounded-full border text-lg font-medium transition
                ${
                  tab === activeButton
                    ? 'bg-[#1B3BB9] text-white border-[#1B3BB9] hover:shadow-2xl hover:border-gray-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#1B3BB9] hover:shadow-2xl hover:text-[#1B3BB9]'
                }`}
              >
                {tab}
              </motion.button>
            ))}
          </div>

          {/* Lower Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {sectorsToShow.map((tab, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveButton2(tab.shortName)}
                className={`px-4 py-2 rounded-full border text-lg font-medium transition
                ${
                  tab.shortName === activeButton2
                    ? 'bg-[#FFD600] text-gray-700 border-[#FFD600] hover:shadow-2xl'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#1B3BB9] hover:shadow-2xl hover:text-[#1B3BB9]'
                }`}
              >
                {tab.name}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Background Image */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-y-auto rounded-xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 1 }}
      >
        <img
          src={BgTop}
          alt="background shape"
          className="relative top-0 left-0 w-full h-auto object-contain opacity-30"
        />
      </motion.div>

      {/* Roadmap or CareerMatching section */}
      <motion.div
        className="mt-10 mb-10"
        key={activeButton2 + activeButton}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {activeButton === 'Emerging Pathways' || activeButton === 'Top-3 Best-Fit' ? (
          <RoadmapPage1 sector={activeButton2} />
        ) : null}
      </motion.div>

      {/* Action Buttons */}
      {/* <motion.div
        className={`flex ${
          activeButton === 'Emerging Pathways' ? 'hidden' : 'block'
        } flex-col md:flex-row justify-between items-center gap-6 w-full mx-auto mt-96 md:my-auto py-10 px-6`}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setActiveButton('Top-3 Best-Fit')}
          className={`px-4 py-2 rounded-full border text-lg font-medium transition ${
            activeButton == 'Top-3 Best-Fit'
              ? 'bg-[#1B3BB9] text-white border-[#1B3BB9] hover:shadow-2xl'
              : 'bg-white text-gray-700 border-gray-600 hover:border-[#1B3BB9] hover:shadow-2xl hover:text-[#1B3BB9]'
          }`}
        >
          Compare to My Profile
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setActiveButton('Emerging Pathways')}
          className={`px-4 py-2 rounded-full border text-lg font-medium transition ${
            activeButton == 'Emerging Pathways'
              ? 'bg-[#1B3BB9] text-white border-[#1B3BB9] hover:shadow-2xl'
              : 'bg-white text-gray-700 border-gray-600 hover:border-[#1B3BB9] hover:shadow-2xl hover:text-[#1B3BB9]'
          }`}
        >
          View Roadmap
        </motion.button>
      </motion.div> */}
    </motion.div>
  );
};

export const RoadMapDashboardPage: React.FC = () => {
  return <AuthenticatedPage render={() => <RoadMapDashboard />} />;
};

//
